import React, { useCallback, useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { IdleTimerProvider } from 'react-idle-timer';
import { useRouter } from 'next/compat/router';
import { useObserver } from 'mobx-react-lite';
import { useApolloClient } from '@apollo/client';

import useSwitchMenuType from 'src/hooks/use-switch-menu-type';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useStores from 'shared/hooks/use-stores';
import useCart from 'shared/hooks/use-cart';
import useUI from 'src/hooks/use-ui';
import { isRecMed } from 'shared/core/helpers/dispensaries';
import MetaTags from 'src/components/meta-tags';
import { FullPageLoader } from 'shared/components';
import { KioskProTutorialModal } from 'src/components/kiosk-pro/tutorial-modal';
import { useKioskIdentityVisibility } from 'src/hooks/use-kiosk-identity-visibility';
import { useKioskDisplayName } from 'src/hooks/use-kiosk-display-name';
import { useKioskScreensaverText } from './use-kiosk-screensaver-text';
import { teardownKioskSession } from '../kiosk-pro-shared/kiosk-session-teardown';
import { useCreatePublicContextSession } from '../kiosk-pro-shared/use-create-public-context-session';
import { KioskProLogo } from '../kiosk-pro-shared/kiosk-pro-logo';
import {
  LockScreenWrapper,
  LogoSection,
  ContentSection,
  CTAGroup,
  PrimaryButton,
  SecondaryButton,
  SubText,
  AndSign,
  LockScreenTitle,
  MenuTypeTitle,
  LockScreenTitleContainer,
  KioskIdentityLabel,
  KioskIdentityTapRegion,
} from './kiosk-pro-lock-screen.styles';

type MenuType = 'med' | 'rec';

type MenuOption = {
  key: MenuType;
  label: string;
  description: string;
  isPrimary: boolean;
};

const MENU_OPTIONS: MenuOption[] = [
  {
    key: 'rec',
    label: 'Recreational',
    description: 'No medical card required',
    isPrimary: true,
  },
  {
    key: 'med',
    label: 'Medical',
    description: 'Medical card required',
    isPrimary: false,
  },
];

export function KioskProLockScreen(): JSX.Element | null {
  const router = useRouter();
  const flags = useFlags();
  const isTutorialEnabled = flags['growth.ecomm.kiosk-pro-tutorial.rollout'] ?? false;
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const { dispensary, loading } = useDispensary();
  const { User, Modals } = useStores();
  const Cart = useCart();
  const UI = useUI();
  const apolloClient = useApolloClient();
  const cName = String(dispensary?.cName ?? '');
  const dispensaryId = dispensary?.id;
  const dispensaryUrl = `/kiosks/${cName}`;
  const isUserLoggedIn = useObserver(() => User?.isLoggedIn);
  const kioskDisplayName = useKioskDisplayName(Cart?.originKioskId ?? null);
  const { isHidden: isKioskIdentityHidden, registerTap } = useKioskIdentityVisibility();

  const {
    createPublicContextSession: createSession,
    isLoading: createPublicContextSessionLoading,
  } = useCreatePublicContextSession();

  // Logout user if they are logged in when this component mounts
  useEffect(() => {
    if (isUserLoggedIn) {
      User?.logout();
    }
  }, [isUserLoggedIn, User]);

  // Kiosks are long-lived tabs; this refetches screensaverText on mount and focus so an Admin
  // change doesn't stay stale for the next customer session. Uses its own dedicated query
  // rather than useDispensary()'s shared cache, so no shared-hook behavior changes app-wide.
  const liveScreensaverText = useKioskScreensaverText(dispensaryId);

  const createPublicContextSession = useCallback(async (): Promise<void> => {
    if (!dispensaryId) {
      return;
    }

    // Starting a new order, unlike mounting the lock screen via browser Back, ends the
    // previous customer's session. Wait for all customer state to clear before creating one.
    await teardownKioskSession({
      user: User,
      cart: Cart,
      ui: UI,
      apolloClient,
      dispensaryId,
      modals: Modals,
    });

    const sessionId = await createSession(dispensaryId);

    if (sessionId) {
      void router?.push({
        pathname: `${dispensaryUrl}/login/public-session`,
        query: { 'session-id': sessionId },
      });
    }
  }, [dispensaryId, User, Cart, UI, apolloClient, Modals, createSession, router, dispensaryUrl]);

  const switchMenuType = useSwitchMenuType();

  const handleMenuTypeClick = useCallback(
    async (menuType: MenuType) => {
      const switchSucceeded = await switchMenuType(menuType);
      if (switchSucceeded) {
        await createPublicContextSession();
      }
    },
    [switchMenuType, createPublicContextSession]
  );

  if (loading) {
    return <FullPageLoader />;
  }

  if (!dispensary) {
    return null;
  }

  const showMenuType = isRecMed(dispensary);
  const tutorialIdleDelay = (dispensary.orderTypesConfigV2?.kiosk?.timeout?.duration ?? 30) * 1000;
  const isDutchiePayEnabledForDispo = dispensary?.paymentTypesAccepted?.dutchiePay ?? false;

  // Kiosk Pro-only screensaver override; whitespace-only is treated as blank (falls back below).
  // liveScreensaverText is undefined only while the dedicated query hasn't loaded yet (use the
  // initial dispensary payload); once loaded it's authoritative, including '' for a cleared value.
  const customScreensaverText = (
    liveScreensaverText ??
    dispensary?.orderTypesConfigV2?.kiosk?.screensaverText ??
    ''
  ).trim();

  const defaultPageTitle = isDutchiePayEnabledForDispo ? (
    <>
      Order <AndSign>&</AndSign> Pay Here
    </>
  ) : (
    'Order Here'
  );
  const pageTitle = customScreensaverText.length > 0 ? customScreensaverText : defaultPageTitle;

  return (
    <>
      <MetaTags />

      <IdleTimerProvider
        timeout={tutorialIdleDelay}
        onIdle={() => setIsTutorialOpen(true)}
        disabled={!isTutorialEnabled || isTutorialOpen}
      >
        <LockScreenWrapper data-testid='kiosk-pro-lock-screen'>
          <LogoSection>
            <KioskProLogo maxHeight='160px' data-testid='kiosk-pro-logo' />
          </LogoSection>

          <ContentSection>
            <LockScreenTitleContainer>
              <LockScreenTitle>{pageTitle}</LockScreenTitle>
            </LockScreenTitleContainer>

            <CTAGroup>
              {showMenuType && <MenuTypeTitle>Select order type to begin:</MenuTypeTitle>}

              {showMenuType ? (
                MENU_OPTIONS.map((option) => {
                  const ButtonComponent = option.isPrimary ? PrimaryButton : SecondaryButton;
                  return (
                    <ButtonComponent
                      key={option.key}
                      onClick={() => handleMenuTypeClick(option.key)}
                      loading={createPublicContextSessionLoading}
                    >
                      {option.label}
                      <SubText>{option.description}</SubText>
                    </ButtonComponent>
                  );
                })
              ) : (
                <PrimaryButton
                  onClick={createPublicContextSession}
                  loading={createPublicContextSessionLoading}
                  data-testid='kiosk-pro-start-order-button'
                >
                  Start Order
                </PrimaryButton>
              )}
              {isTutorialEnabled && (
                <SecondaryButton
                  type='button'
                  onClick={() => setIsTutorialOpen(true)}
                  data-testid='kiosk-pro-lock-help'
                >
                  How it works
                </SecondaryButton>
              )}
            </CTAGroup>
          </ContentSection>

          {kioskDisplayName &&
            (isKioskIdentityHidden ? (
              <KioskIdentityTapRegion data-testid='kiosk-identity-restore-region' onClick={registerTap} />
            ) : (
              <KioskIdentityLabel data-testid='kiosk-identity-indicator' onClick={registerTap}>
                Kiosk: {kioskDisplayName}
              </KioskIdentityLabel>
            ))}
        </LockScreenWrapper>
      </IdleTimerProvider>

      {isTutorialEnabled && <KioskProTutorialModal isOpen={isTutorialOpen} onClose={() => setIsTutorialOpen(false)} />}
    </>
  );
}
